// src/store/slices/brandSlice.ts
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'

import apiClient from '@/libs/apiClient'
import type { Brand, BrandFormData } from '@/types/catalog'

interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

interface BrandState {
  brands: Brand[]
  pagination: Pagination | null
  loading: boolean
  error: string | null
}

const initialState: BrandState = {
  brands: [],
  pagination: null,
  loading: false,
  error: null
}

// ============================
// Fetch all brands
// ============================
export const fetchBrands = createAsyncThunk<
  { brands: Brand[]; pagination: Pagination },
  { limit?: number } | undefined,
  { rejectValue: string }
>('brands/fetchBrands', async (params = {}, { rejectWithValue }) => {
  try {
    const limit = params.limit || 1000
    const res = await apiClient.get('/admin/v1/brands', { params: { limit } })
    const json = res.data

    const pagination: Pagination = {
      currentPage: json.meta.currentPage,
      lastPage: json.meta.lastPage,
      perPage: json.meta.perPage,
      total: json.meta.total
    }

    return { brands: json.data, pagination }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch brands')
  }
})

// ============================
// Add Brand
// ============================
export const addBrand = createAsyncThunk<Brand, FormData, { rejectValue: string }>(
  'brands/addBrand',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/brands/add', formData)
      const json = res.data

      return json.data as Brand
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add brand')
    }
  }
)

// ============================
// Update Brand
// ============================
export const updateBrand = createAsyncThunk<Brand, { id: string; formData: FormData }, { rejectValue: string }>(
  'brands/updateBrand',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/brands/${id}/edit`, formData)
      const json = res.data

      return json.data as Brand
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to update brand')
    }
  }
)

// ============================
// Delete Brand
// ============================
export const deleteBrand = createAsyncThunk<string, string, { rejectValue: string }>(
  'brands/deleteBrand',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/brands/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete brand')
    }
  }
)

// ============================
// Toggle Brand Status
// ============================
export const toggleBrand = createAsyncThunk<Brand, { id: string; is_enabled: boolean }, { rejectValue: string }>(
  'brands/toggleBrand',
  async ({ id, is_enabled }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/brands/${id}/toggle`, { is_enabled })

      return res.data.data as Brand
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to toggle brand status')
    }
  }
)

// ============================
// Slice
// ============================
const brandSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // Fetch
      .addCase(fetchBrands.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBrands.fulfilled, (state, action: PayloadAction<{ brands: Brand[]; pagination: Pagination }>) => {
        state.loading = false
        state.brands = action.payload.brands
        state.pagination = action.payload.pagination
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch brands'
      })

      // Add
      .addCase(addBrand.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addBrand.fulfilled, (state, action: PayloadAction<Brand>) => {
        state.loading = false
        state.brands.push(action.payload)
        if (state.pagination) state.pagination.total += 1
      })
      .addCase(addBrand.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to add brand'
      })

      // Update
      .addCase(updateBrand.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateBrand.fulfilled, (state, action: PayloadAction<Brand>) => {
        state.loading = false
        state.brands = state.brands.map(b => (b.id === action.payload.id ? action.payload : b))
      })
      .addCase(updateBrand.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to update brand'
      })

      // Delete
      .addCase(deleteBrand.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteBrand.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false
        state.brands = state.brands.filter(b => b.id !== action.payload)
        if (state.pagination) state.pagination.total -= 1
      })
      .addCase(deleteBrand.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to delete brand'
      })

      // Toggle
      .addCase(toggleBrand.fulfilled, (state, action: PayloadAction<Brand>) => {
        state.brands = state.brands.map(b => (b.id === action.payload.id ? action.payload : b))
      })
  }
})

export default brandSlice.reducer
